import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { darkTheme, styled, vh } from "~/ui/style/stitches.config";
import { getGuidebookMeaning, GuidebookMeaning as GuidebookMeaningType, MeaningContent } from "~/api/guidebookApi";
import { imageUrl } from "~/utils/imageurl";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { HoloCard } from "~/ui/components/HoloCard";
import { CardArtVariations } from "./CardArtVariations";
import { trackEvent } from "~/api/analyticsApi";
import { SubscriptionCTA } from "~/ui/components/SubscriptionCTA";
import { AuthContext } from "~/utils/useAuth";
import { isInTrial } from "~/api/userApi";
import { FeatureFeedbackForm } from "~/ui/components/FeatureFeedbackForm";

// Locked section component with blurred text placeholder
const LockedSection = ({ name }: { name: string }) => {
  const { user } = useContext(AuthContext);

  // Use window.location.href so iOS native app can intercept navigation to show native paywall
  const handleClick = () => {
    trackEvent("/guidebook/pressed_paywall_section", { section: name });
    window.location.href = user ? "/plans" : "/signup";
  };

  return (
    <LockedSectionWrapper>
      <LockedSectionTitle>{name}</LockedSectionTitle>
      <LockedContentBox>
        <FakeTextLine />
        <FakeTextLine />
        <FakeTextLine />
        <FakeTextLine style={{ width: "55%" }} />
        <UnlockButton onClick={handleClick}>
          View
        </UnlockButton>
      </LockedContentBox>
    </LockedSectionWrapper>
  );
};

// Reusable content component that can be embedded in the overlay
export const GuidebookMeaningContent = ({ slug }: { slug: string }) => {
  const [meaning, setMeaning] = useState<GuidebookMeaningType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReversed, setIsReversed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const trackedSlug = useRef<string | null>(null);

  useEffect(() => {
    const fetchMeaning = async () => {
      try {
        setLoading(true);
        setError(null);
        setImageLoaded(false);
        setIsReversed(false);
        const response = await getGuidebookMeaning(slug);
        setMeaning(response.meaning);
        if (trackedSlug.current !== slug) {
          trackedSlug.current = slug;
          trackEvent("/guidebook/viewed_meaning", { cardName: response.meaning.name });
        }
      } catch (err) {
        console.error("Failed to load meaning:", err);
        setError("Can't find guidebook entry");
      } finally {
        setLoading(false);
      }
    };
    fetchMeaning();
  }, [slug]);

  if (loading) {
    return (
      <LoadingWrapper>
        <SimpleLoadingIndicator style={{ position: "static", width: "30px", height: "30px" }} />
      </LoadingWrapper>
    );
  }

  if (error || !meaning) {
    return (
      <ErrorWrapper>
        <ErrorMessage>{error || "Something went wrong"}</ErrorMessage>
      </ErrorWrapper>
    );
  }

  // Get content with fallback: use reversed if available, otherwise upright
  const content: MeaningContent = isReversed && meaning.reversed
    ? {
        keywords: meaning.reversed.keywords?.length ? meaning.reversed.keywords : meaning.upright.keywords,
        overview: meaning.reversed.overview || meaning.upright.overview,
        sections: meaning.reversed.sections?.length ? meaning.reversed.sections : meaning.upright.sections,
        images: meaning.reversed.images || meaning.upright.images,
        imagesLocked: meaning.reversed.imagesLocked ?? meaning.upright.imagesLocked,
      }
    : meaning.upright;

  // Get image categories with their labels
  const imageCategories = content.images
    ? Object.entries(content.images)
    : [];

  // Get user to check trial status
  const { user } = useContext(AuthContext);
  const inTrial = user && isInTrial(user);

  // Show CTA if there's locked content OR user is in trial (to remind them to subscribe)
  const hasLockedContent =
    content.sections?.some(s => s.isLocked) ||
    content.imagesLocked;
  const showSubscriptionCTA = hasLockedContent || inTrial;

  return (
    <MeaningContentWrapper>
      {/* Card Image with Reverse Button */}
      {meaning.imagePath && (
        <CardSection style={{ opacity: imageLoaded ? 1 : 0 }}>
          <CardImageContainer>
            <HoloCard width={180} height={291} restingRotateX={5} borderRadius={11}>
              <CardImage
                src={imageUrl(meaning.imagePath, 400)}
                alt={meaning.name}
                onLoad={() => setImageLoaded(true)}
                style={{
                  transform: isReversed ? "rotate(180deg)" : undefined,
                }}
              />
            </HoloCard>
          </CardImageContainer>
          {meaning.reversed && (
            <ReverseButton onClick={() => setIsReversed(!isReversed)}>
              <img
                src="/images/room/reverse-tool.svg"
                alt="Flip card"
                style={{ transform: isReversed ? "scaleY(1)" : "scaleY(-1)" }}
              />
              <ReverseLabel>flip</ReverseLabel>
            </ReverseButton>
          )}
        </CardSection>
      )}

      {/* Card Name */}
      <CardName>
        {meaning.name}{isReversed ? " (Reversed)" : ""}
      </CardName>

      {/* Keywords */}
      {content.keywords && content.keywords.length > 0 && (
        <Keywords>{content.keywords.join(" · ")}</Keywords>
      )}

      {/* Overview */}
      {content.overview && (
        <Overview>{content.overview}</Overview>
      )}

      {/* Sections in order: Relationships, Creativity, Work, Journal Questions, etc. */}
      {content.sections?.map((section, index) => {
        if (section.isLocked) {
          return <LockedSection key={index} name={section.name} />;
        }

        // Journal questions render as a list of bold questions
        if (section.name.toLowerCase() === 'journal questions' && section.content) {
          const questions = section.content.split('\n').filter(q => q.trim());
          return (
            <SubSection key={index}>
              <SubSectionTitle>{section.name}</SubSectionTitle>
              <JournalQuestionsList>
                {questions.map((question, qIndex) => (
                  <JournalQuestion key={qIndex}>{question}</JournalQuestion>
                ))}
              </JournalQuestionsList>
            </SubSection>
          );
        }

        return (
          <SubSection key={index}>
            <SubSectionTitle>{section.name}</SubSectionTitle>
            <SubSectionContent>{section.content}</SubSectionContent>
          </SubSection>
        );
      })}

      {/* Vibes / associated images */}
      {imageCategories.length > 0 ? (
        imageCategories.map(([category, images]) => (
          <AssociatedImages key={category}>
            <ImageLabel>{category === "meme" ? "Memes" : category}</ImageLabel>
            {images.map((imgPath, index) => (
              <AssociatedImage
                key={index}
                src={imageUrl(imgPath, 600)}
                alt=""
              />
            ))}
          </AssociatedImages>
        ))
      ) : content.imagesLocked ? (
        <LockedSection name="Memes" />
      ) : null}

      {/* CTA for paywalled content or trial users */}
      {showSubscriptionCTA && <SubscriptionCTA />}

      {/* Deck Art - same card from other decks */}
      <CardArtVariations
        cardName={meaning.name}
        title={<SubSectionTitle>Deck Art</SubSectionTitle>}
      />

      {/* Feedback form - only show when logged in */}
      {user && (
        <FeatureFeedbackForm
          formId="mB6KVA"
          description="The guidebook is new! Let us know what you think."
          feature="guidebook"
          spread={isReversed ? `${meaning.name} (Reversed)` : meaning.name}
        />
      )}
    </MeaningContentWrapper>
  );
};

// Full page wrapper - used when accessing /guidebook/:slug directly
// This will redirect to GuidebookIndex with the overlay open
export const GuidebookMeaningPage = ({ slug }: { slug: string }) => {
  const [, setLocation] = useLocation();

  // Redirect to guidebook with slug param to open overlay
  useEffect(() => {
    setLocation(`/guidebook?card=${slug}`, { replace: true });
  }, [slug, setLocation]);

  // Show loading while redirecting
  return (
    <StyledMeaningPage>
      <SimpleLoadingIndicator />
    </StyledMeaningPage>
  );
};

// Styled components for the full-page redirect view
const StyledMeaningPage = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: vh(100),
  backgroundColor: "$wash",
  color: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10,
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    color: "$text",
  },
});

// Styled components for the meaning content (used in overlay)
const MeaningContentWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  textAlign: "left",
  padding: "0 24px",
  userSelect: "none",
});

const LoadingWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: `calc(${vh(75)} - 50px)`,
  "@mobile": { minHeight: `calc(${vh(100)} - 64px)` },
});

const ErrorWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "200px",
});

const ErrorMessage = styled("div", {
  textAlign: "center",
  fontSize: "12px",
  color: "#000",
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

const CardSection = styled("div", {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "center",
  marginBottom: "16px",
  marginTop: "60px",
  transition: "opacity 0.5s ease-out",
});

const CardImageContainer = styled("div", {});

const CardImage = styled("img", {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "12px",
  border: "1px solid rgba(0, 0, 0, 0.5)",
  [`.${darkTheme.className} &`]: {
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },
});

const ReverseButton = styled("button", {
  position: "absolute",
  right: "-44px",
  top: "50%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  background: "rgba(0, 0, 0, 0)",
  border: "none",
  borderRadius: "7px",
  transform: "translateY(-50%)",
  cursor: "pointer",
  padding: "6px 6px 3px 6px",
  opacity: 1,
  transition: "opacity 0.2s ease",

  "&:hover": {
    opacity: .8,
    background: "rgba(0, 0, 0, 0.05)",
  },
  [`.${darkTheme.className} &`]: {
    "&:hover": {
      background: "rgba(255, 255, 255, 0.1)",
    },
  },

  "& img": {
    width: "20px",
    height: "20px",
    [`.${darkTheme.className} &`]: {
      filter: "invert(1)",
    },
  },
});

const ReverseLabel = styled("span", {
  fontSize: "11px",
  color: "rgba(0, 0, 0, 0.8)",
  lineHeight: 1,
  marginTop: "4px",
  "@mobile": {
    fontSize: "12px",
  },
  [`.${darkTheme.className} &`]: {
    color: "$textMuted",
  },
});

const CardName = styled("h1", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "27px",
  fontWeight: "200",
  margin: "16px 0 8px 0",
  lineHeight: 1.1,
  textAlign: "center",
  alignSelf: "center",
    "@mobile": {
    fontSize: "30px",
  },
});

const Keywords = styled("div", {
  fontSize: "11px",
  fontFamily: "worksans",
  lineHeight: "1.4",
  textTransform: "lowercase",
  marginBottom: "16px",
  textAlign: "center",
  alignSelf: "center",
  maxWidth: "80%",
    "@mobile": {
    fontSize: "12px",
    maxWidth: "90%",
  },
});

const Overview = styled("div", {
  fontSize: "13px",
  fontFamily: "worksans",
  fontWeight: "400",
  lineHeight: 1.4,
  color: "#000",
  marginBottom: "24px",
  backgroundColor: "rgba(129, 129, 129, 0.1)",
  border: "1px solid $gray500",
  padding: "1.2rem",
  borderRadius: "12px",
  width: "100%",
  [`.${darkTheme.className} &`]: {
    color: "$text",
    backgroundColor: "rgba(15, 15, 15, 0.6)",
    border: "1px solid $inputBorder",
  },
});

const SubSection = styled("div", {
  marginBottom: "30px",
  textAlign: "left",

  "&:last-child": {
    marginBottom: 0,
  },
});

const SubSectionTitle = styled("h3", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "23px",
  fontWeight: "400",
  borderRadius: "12px",
  width: "fit-content",
  margin: "0 0 4px 0",
  color: "#000",
  textTransform: "capitalize",
  "@mobile": {
    fontSize: "27px",
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

const SubSectionContent = styled("p", {
  fontSize: "13px",
  lineHeight: 1.4,
  margin: "4px auto 4px",
  color: "#000",
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

const JournalQuestionsList = styled("div", {
  margin: 0,
  padding: 0,
  textAlign: "left",
});

const JournalQuestion = styled("p", {
  fontSize: "13px",
  fontWeight: "500",
  lineHeight: 1.4,
  color: "#000",
  marginBottom: "8px",

  "&:last-child": {
    marginBottom: 0,
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

const AssociatedImages = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  marginTop: "8px",
  marginBottom: "24px",
  width: "100%",
});

const ImageLabel = styled("h3", {
  fontFamily: "KeplerLightCondensed",
  textAlign: "left",
  fontSize: "23px",
  fontWeight: "400",
  margin: "0",
  color: "#000",
  textTransform: "capitalize",
  alignSelf: "flex-start",
  "@mobile": {
    fontSize: "27px",
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

const AssociatedImage = styled("img", {
  width: "100%",
  maxWidth: "300px",
  aspectRatio: "1 / 1",
  objectFit: "cover",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
  [`.${darkTheme.className} &`]: {
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
});

// Locked section styles
const LockedSectionWrapper = styled("div", {
  marginBottom: "30px",
  width: "100%",
});

const LockedSectionTitle = styled("h3", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "23px",
  fontWeight: "400",
  margin: "0 0 12px 0",
  color: "#000",
  textTransform: "capitalize",
  "@mobile": {
    fontSize: "27px",
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

const LockedContentBox = styled("div", {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  width: "100%",
  boxSizing: "border-box",
});

const FakeTextLine = styled("div", {
  height: "14px",
  borderRadius: "6px",
  backgroundColor: "rgba(0, 0, 0, 0.06)",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
});

const UnlockButton = styled("button", {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  fontFamily: "worksans",
  fontSize: "13px",
  fontWeight: "500",
  color: "#000",
  backgroundColor: "#fff",
  border: "1px solid #aeaeae",
  borderRadius: "24px",
  padding: "9px 24px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  zIndex: 1,

  "&:hover": {
    backgroundColor: "#f8f8f8",
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
    backgroundColor: "rgba(15, 15, 15, 0.6)",
    border: "1px solid $inputBorder",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.6)",
    },
  },
});
